import type { City } from "./cities-types";

const montclair: City = {
  slug: "montclair",
  name: "Montclair",
  region: "CA",
  lat: 34.0775,
  lng: -117.6897,

  heroEyebrow: "Headquartered in Montclair since day one",
  heroTitle: "Managed IT Services for Montclair, CA Businesses",
  heroSubtitle: "Local IT support that arrives fast and stays patient.",
  heroIntro:
    "We're a small, locally-owned IT shop on Benson Ave — not a national call center routing you through a queue. We support Montclair offices, retail, medical practices, and trades with proactive monitoring, cybersecurity, and on-site help that's usually a short drive away. When a printer dies in your Holt Blvd showroom or a server reboots itself on a Sunday morning, you call a real person who already knows your network.",

  stats: [
    { value: "45 min", label: "Average on-site response in Montclair" },
    { value: "32+", label: "Five-star local reviews" },
    { value: "8 yrs", label: "Serving the Inland Empire" },
  ],

  whyPoints: [
    {
      title: "We actually live here",
      body:
        "Our office is at 8880 N Benson Ave #121, two minutes from Montclair Plaza. When a Montclair client has an emergency, we drive — we don't escalate a ticket to a tech in another time zone.",
    },
    {
      title: "Built for Montclair's mix of businesses",
      body:
        "Logistics yards near Mission Blvd, dental and medical offices off Central Ave, retail along Moreno St — each one has different compliance and uptime needs. We tune our service plans to what your industry actually requires, not a one-size-fits-all template.",
    },
    {
      title: "Predictable pricing for SMB budgets",
      body:
        "No surprise invoices. Our Montclair managed-IT plans are flat-rate per user so you can forecast IT costs the same way you forecast rent — and our owner picks up the phone when you want to talk through them.",
    },
  ],

  neighborhoods: [
    "Montclair Plaza area",
    "Holt Blvd corridor",
    "Mission Blvd / industrial district",
    "Central Ave business park",
    "Benson Ave & Arrow Hwy",
    "Moreno St retail strip",
  ],

  industries: [
    {
      name: "Medical & Dental",
      body:
        "HIPAA-aware backup, secure email, and workstation hardening for Montclair clinics and specialty practices.",
    },
    {
      name: "Logistics & Warehousing",
      body:
        "Reliable Wi-Fi across yards near Mission and Holt, ruggedized scanner support, and uptime monitoring that pages us before drivers notice.",
    },
    {
      name: "Professional Services",
      body:
        "Microsoft 365, secure document handling, and MFA rollouts for Montclair law, accounting, and insurance offices.",
    },
    {
      name: "Retail & Hospitality",
      body:
        "Point-of-sale support, PCI-aware network segmentation, and after-hours patching so your registers are ready when doors open.",
    },
  ],

  responseTime: "Most Montclair on-site visits are filled within 45 minutes during business hours.",
  driveTime: "About 12 minutes from our Benson Ave office to most Montclair business addresses.",

  testimonial: {
    name: "Malcolm B.",
    business: "Montclair small business owner",
    quote:
      "Armands is the IT guy you wish you'd hired five years ago. He showed up the same morning, fixed the immediate issue, then walked us through what to change so it wouldn't happen again. Worth every penny.",
  },

  caseStudy: {
    title: "Montclair dental office — recovered from ransomware in under a day",
    body:
      "A 9-operator practice off Central Ave was hit by ransomware on a Friday afternoon. We were on-site within the hour, isolated the affected workstations, restored from the off-site backups we'd set up the year before, and had patients being checked in by Monday morning — without paying the ransom.",
    result: "Zero patient data lost. Zero days closed.",
  },

  faqs: [
    {
      q: "Do you offer on-site IT support in Montclair, CA?",
      a: "Yes — our office is at 8880 N Benson Ave #121 in Montclair, so on-site visits across the city usually happen the same day, often within 45 minutes during business hours.",
    },
    {
      q: "What size Montclair businesses do you support?",
      a: "We work best with Montclair-area teams from 5 to about 150 users — small offices, medical and dental practices, professional services, light industrial, and multi-site retail.",
    },
    {
      q: "Can you help a Montclair business that already has an IT person?",
      a: "Absolutely. We co-manage with internal IT staff in plenty of Montclair offices — handling after-hours coverage, security projects, or specialist work your in-house tech doesn't have bandwidth for.",
    },
    {
      q: "What does managed IT typically cost for a Montclair business?",
      a: "Most Montclair clients land between $99–$175 per user per month depending on coverage, compliance needs, and after-hours requirements. We quote flat rates after a free on-site assessment — no surprise invoices.",
    },
    {
      q: "How fast can you start?",
      a: "For Montclair businesses we can usually run the discovery assessment within a week and start active monitoring the following week. Emergency cutovers (a current MSP failing you, for example) we've done in under 48 hours.",
    },
  ],
};

const ranchoCucamonga: City = {
  slug: "rancho-cucamonga",
  name: "Rancho Cucamonga",
  region: "CA",
  lat: 34.1064,
  lng: -117.5931,

  heroEyebrow: "Dispatched from Montclair, working across Rancho Cucamonga",
  heroTitle: "Managed IT Services in Rancho Cucamonga, CA",
  heroSubtitle: "IT support built around medical, dental and professional-services offices.",
  heroIntro:
    "Rancho Cucamonga runs on appointment-driven businesses: dental and medical suites along Foothill Blvd, physical therapy and imaging offices near Haven Ave, and the accounting, title, insurance and real-estate firms clustered around Victoria Gardens and the Civic Center. When an EHR client won't launch or a scanner drops off the network, the schedule backs up and the day's revenue goes with it. SimpleTech supports those offices with proactive monitoring, patient-data-aware backups, and a technician who knows your practice management software by name.",

  metaTitle: "Rancho Cucamonga Managed IT Services — (909) 255-9112",
  metaDescription:
    "A frozen EHR or CRM costs Rancho Cucamonga, CA offices billable hours. Managed IT, cybersecurity and VoIP dispatched from nearby Montclair. Call (909) 255-9112.",

  stats: [
    { value: "~15 min", label: "Drive from our Montclair office" },
    { value: "32+", label: "Five-star local reviews" },
    { value: "8 yrs", label: "Serving the Inland Empire" },
  ],

  whyPoints: [
    {
      title: "Built for appointment-based offices",
      body:
        "Practice management, EHR and imaging software behave differently than generic office apps. We test patches against them before rolling them out so an update doesn't land in the middle of your morning schedule.",
    },
    {
      title: "One drive up Haven or Milliken",
      body:
        "We're based in Montclair, not Los Angeles. Most Rancho Cucamonga addresses are a short freeway hop, so on-site work is scheduled the same week — often the same day — rather than queued behind a metro dispatch list.",
    },
    {
      title: "Documentation your staff can actually use",
      body:
        "Front-desk turnover is real. We leave plain-English runbooks for the things staff touch daily — printers, scanners, phones, shared drives — so a new hire isn't a support ticket.",
    },
  ],

  neighborhoods: [
    "Foothill Blvd corridor",
    "Victoria Gardens area",
    "Haven Ave professional offices",
    "Milliken Ave business parks",
    "Terra Vista",
    "Etiwanda / Day Creek",
  ],

  industries: [
    {
      name: "Medical & Dental",
      body:
        "HIPAA-aware backups, encrypted email, and workstation hardening for practices along Foothill and Baseline.",
    },
    {
      name: "Professional Services",
      body:
        "Microsoft 365, MFA, and secure document workflows for accounting, title, insurance and legal offices.",
    },
    {
      name: "Real Estate & Property Management",
      body:
        "CRM integrations, e-signature workflows, and mobile device support for agents working out of the car.",
    },
    {
      name: "Retail & Restaurants",
      body:
        "POS network segmentation and reliable guest Wi-Fi around Victoria Gardens and Terra Vista.",
    },
  ],

  responseTime:
    "On-site visits in Rancho Cucamonga are typically scheduled same-day or next business day, with remote support starting immediately.",
  driveTime: "Roughly 15–20 minutes from our Montclair office to most Rancho Cucamonga business addresses.",

  faqs: [
    {
      q: "Do you have an office in Rancho Cucamonga?",
      a: "No — we're honest about that. SimpleTech is based at 8880 N Benson Ave #121, Montclair, CA 91763 and dispatches technicians to Rancho Cucamonga. It's a 15–20 minute drive, so on-site work is usually scheduled same-day or next business day while remote support begins right away.",
    },
    {
      q: "Can you support our practice management or EHR software?",
      a: "Yes. We don't replace your vendor's application support, but we own everything around it: workstations, servers or cloud tenants, backups, imaging peripherals, and the network. We also coordinate directly with software vendors so your office manager isn't stuck translating between two companies.",
    },
    {
      q: "We're a 6-person office in Rancho Cucamonga. Are we too small for managed IT?",
      a: "No. Plenty of our Rancho Cucamonga clients are between 5 and 25 users. Small offices actually feel the benefit fastest, because one unmanaged laptop or an expired backup is a bigger share of your operation than it would be at a 200-person company.",
    },
    {
      q: "What happens the first month we switch to SimpleTech?",
      a: "We start with an assessment of your systems and document what we find, then fix the highest-risk items first — usually backups, MFA and unpatched machines. After that we move you onto ongoing monitoring and a flat monthly plan, and you get a written summary of what changed.",
    },
  ],
};

const upland: City = {
  slug: "upland",
  name: "Upland",
  region: "CA",
  lat: 34.0975,
  lng: -117.6484,

  heroEyebrow: "A few minutes north of our Montclair office",
  heroTitle: "Managed IT Services in Upland, CA",
  heroSubtitle: "Practical IT for independent shops, family practices and small offices.",
  heroIntro:
    "Upland's business base is small and independent: boutiques and service shops along Foothill Blvd and Historic Downtown, family medical and dental practices near San Antonio Regional Hospital, salons, studios, contractors and single-office professional firms. Most of them have no IT staff at all — the owner is the help desk. SimpleTech takes that role over: patching, backups, email security, phones and the printer that only misbehaves on Mondays, handled by a local team you can actually reach.",

  metaTitle: "Upland CA Managed IT Services — (909) 255-9112",
  metaDescription:
    "Upland, CA shops and family practices lose sales when systems stall. Managed IT, cybersecurity and VoIP from our nearby Montclair office. Call (909) 255-9112.",

  stats: [
    { value: "~8 min", label: "Drive from our Montclair office" },
    { value: "32+", label: "Five-star local reviews" },
    { value: "8 yrs", label: "Serving the Inland Empire" },
  ],

  whyPoints: [
    {
      title: "Closest city to our office",
      body:
        "Upland is essentially next door — a short drive up Benson or Central. That makes hands-on work like a new workstation rollout, a cabling fix or a phone cutover easy to schedule without a big travel surcharge.",
    },
    {
      title: "Sized for owner-operated businesses",
      body:
        "You don't need an enterprise stack. We start with the four things that actually protect a small Upland business: verified backups, MFA on email, managed endpoint protection, and someone who answers the phone.",
    },
    {
      title: "No jargon, ever",
      body:
        "We explain what we're doing and what it costs in plain language before we touch anything. If a cheaper option is the right call for your shop, we'll say so.",
    },
  ],

  neighborhoods: [
    "Historic Downtown Upland",
    "Foothill Blvd corridor",
    "Euclid Ave",
    "San Antonio Regional Hospital area",
    "Mountain Ave business strip",
    "Campus Ave / Arrow Route",
  ],

  industries: [
    {
      name: "Retail & Boutiques",
      body:
        "Point-of-sale reliability, card-payment network hygiene, and Wi-Fi that keeps the register and the customer traffic separated.",
    },
    {
      name: "Family Medical & Dental",
      body:
        "HIPAA-aware backup and recovery, secure email, and workstation lockdown for independent Upland practices.",
    },
    {
      name: "Contractors & Trades",
      body:
        "Mobile device support, cloud file access from the job site, and estimating software that works away from the office.",
    },
    {
      name: "Professional & Personal Services",
      body:
        "Microsoft 365 setup, scheduling and CRM integrations, and phone systems for salons, studios and single-office firms.",
    },
  ],

  responseTime:
    "Because Upland borders Montclair, on-site help is often available the same business day; remote support starts as soon as you call.",
  driveTime: "About 8–12 minutes from our Montclair office to most Upland business addresses.",

  faqs: [
    {
      q: "How quickly can you get to an Upland business?",
      a: "Upland is the closest city to our Montclair office — usually under 15 minutes. Remote support starts as soon as you call (909) 255-9112, and on-site visits are commonly available the same business day depending on the day's schedule.",
    },
    {
      q: "We only have four computers. Is managed IT worth it?",
      a: "For many small Upland businesses, yes — but not always. If you have four machines and no shared server, a lighter plan covering backups, email security and endpoint protection is usually the right fit. We'll tell you honestly if hourly support makes more sense for you.",
    },
    {
      q: "Can you help with our phones as well as our computers?",
      a: "Yes. VoIP is a large part of what we do for Upland retail and practices — porting your existing number, setting up call routing and voicemail-to-email, and cutting the monthly bill compared with legacy phone lines.",
    },
    {
      q: "Do you support Upland businesses with more than one location?",
      a: "We do. Multi-site work in Upland usually means standardizing the network at each site, centralizing files in Microsoft 365 or SharePoint, and putting both locations on one phone system so calls can roll between them.",
    },
  ],
};

const claremont: City = {
  slug: "claremont",
  name: "Claremont",
  region: "CA",
  lat: 34.0967,
  lng: -117.7198,

  heroEyebrow: "Dispatched from Montclair, one city over",
  heroTitle: "Managed IT Services in Claremont, CA",
  heroSubtitle: "IT support for professional firms, nonprofits and college-adjacent organizations.",
  heroIntro:
    "Claremont's economy leans academic and professional: the Claremont Colleges and their vendor ecosystem, nonprofits and foundations, law and accounting practices around the Village, therapy and counseling offices, plus independent retail and restaurants on Yale Ave. These organizations handle sensitive records — client files, donor data, student and research information — usually without a dedicated IT department. SimpleTech provides that department: documented systems, tested backups, security controls that stand up to a grant or insurance review, and support that arrives from one city away.",

  metaTitle: "Claremont CA Managed IT Services — (909) 255-9112",
  metaDescription:
    "Claremont, CA firms and nonprofits can't afford lost client files. Managed IT, cybersecurity and VoIP dispatched from nearby Montclair. Call (909) 255-9112.",

  stats: [
    { value: "~10 min", label: "Drive from our Montclair office" },
    { value: "32+", label: "Five-star local reviews" },
    { value: "8 yrs", label: "Serving the Inland Empire" },
  ],

  whyPoints: [
    {
      title: "Comfortable with confidentiality requirements",
      body:
        "Legal, counseling and nonprofit work carries real confidentiality duties. We build access controls, encrypted backups and audit-friendly documentation so you can answer questions from a client, funder or insurer with evidence rather than assurances.",
    },
    {
      title: "One city away, not one county away",
      body:
        "Our office is in Montclair, roughly ten minutes from the Claremont Village. Nothing about serving Claremont requires a freeway crawl in from Los Angeles or Orange County.",
    },
    {
      title: "Grant- and budget-aware planning",
      body:
        "Nonprofits and small firms plan in fiscal years, not sales quarters. We map hardware refreshes and license renewals to your budget cycle and flag nonprofit pricing where Microsoft and other vendors offer it.",
    },
  ],

  neighborhoods: [
    "Claremont Village",
    "Yale Ave & Indian Hill Blvd",
    "Claremont Colleges area",
    "Foothill Blvd corridor",
    "Base Line Rd offices",
    "Monte Vista / Arrow Hwy",
  ],

  industries: [
    {
      name: "Legal & Professional Services",
      body:
        "Document security, MFA, encrypted email and retention-aware backups for Claremont law, accounting and consulting practices.",
    },
    {
      name: "Nonprofits & Foundations",
      body:
        "Donor database protection, Microsoft 365 nonprofit tenants, and volunteer-friendly account management.",
    },
    {
      name: "Counseling & Health Practices",
      body:
        "HIPAA-aware telehealth setups, secure intake forms, and workstation privacy controls.",
    },
    {
      name: "Retail, Dining & Education Support",
      body:
        "Reliable guest Wi-Fi, POS support, and AV/conferencing setups for Village businesses and college-adjacent organizations.",
    },
  ],

  responseTime:
    "Claremont on-site visits are typically scheduled the same or next business day, with remote support beginning immediately.",
  driveTime: "About 10 minutes from our Montclair office to most Claremont business addresses.",

  faqs: [
    {
      q: "Is SimpleTech located in Claremont?",
      a: "No. Our office is at 8880 N Benson Ave #121, Montclair, CA 91763, about ten minutes from the Claremont Village, and we dispatch technicians into Claremont. We'd rather be straight with you about that than claim an address we don't have.",
    },
    {
      q: "Can you meet the security expectations in our funder or insurance questionnaires?",
      a: "Usually, yes. Those questionnaires typically ask about MFA, endpoint protection, patching, offsite backups, access reviews and incident response. We implement those controls and give you written documentation of each one so your team can answer the questions accurately.",
    },
    {
      q: "Do you work with nonprofit budgets?",
      a: "We do. Several Claremont-area nonprofits run on flat-rate plans sized to what they can actually sustain, and we pursue nonprofit licensing discounts from Microsoft and security vendors wherever they apply rather than quoting commercial list price.",
    },
    {
      q: "What if our staff work from home part of the week?",
      a: "That's common in Claremont professional and academic work. We secure remote access properly — company-managed devices where possible, MFA everywhere, encrypted file access through Microsoft 365 — instead of relying on personal machines and forwarded email.",
    },
  ],
};

const ontario: City = {
  slug: "ontario",
  name: "Ontario",
  region: "CA",
  lat: 34.0633,
  lng: -117.6509,

  heroEyebrow: "Dispatched from Montclair into Ontario every week",
  heroTitle: "Managed IT Services in Ontario, CA",
  heroSubtitle: "IT support for logistics, warehousing and airport-adjacent operations.",
  heroIntro:
    "Ontario is the Inland Empire's freight engine: third-party logistics and warehousing along Jurupa and Fourth St, customs brokers and freight forwarders clustered near ONT, distribution operations off the I-10 and I-15, plus the trades, manufacturers and offices that serve them. Their IT problems are not typical office problems — barcode scanners dropping off Wi-Fi in a 200,000 sq ft building, WMS integrations timing out, and shift work that means there's no safe overnight window. SimpleTech supports that environment with warehouse-grade wireless design, monitored backups, and change work scheduled around your shifts.",

  metaTitle: "Ontario CA Managed IT Services — (909) 255-9112",
  metaDescription:
    "Warehouse and office downtime stalls Ontario, CA shipments. Managed IT, cybersecurity and VoIP dispatched from our Montclair office. Call (909) 255-9112.",

  stats: [
    { value: "~12 min", label: "Drive from our Montclair office" },
    { value: "32+", label: "Five-star local reviews" },
    { value: "8 yrs", label: "Serving the Inland Empire" },
  ],

  whyPoints: [
    {
      title: "We plan for warehouse floors, not just desks",
      body:
        "Wi-Fi that works in a front office fails between steel racking. We survey coverage, place access points for the aisles your pickers actually walk, and keep scanner and printer firmware consistent across the building.",
    },
    {
      title: "Work scheduled around your shifts",
      body:
        "Ontario operations often run well past 5 p.m. We plan patching, switch replacements and cutovers around your shift changes and dock schedule instead of assuming a quiet weeknight.",
    },
    {
      title: "Local dispatch, not a metro queue",
      body:
        "Our office is in Montclair — around 12 minutes from most Ontario business parks. A failed switch or downed access point gets a technician driving, not a ticket escalated to a provider working its way east from LA.",
    },
  ],

  neighborhoods: [
    "ONT airport / Inland Empire Blvd",
    "Jurupa St industrial corridor",
    "Fourth St / Milliken warehouses",
    "Ontario Mills area",
    "Downtown Ontario / Euclid Ave",
    "Guasti & Archibald business parks",
  ],

  industries: [
    {
      name: "Logistics & Warehousing",
      body:
        "Warehouse Wi-Fi coverage, rugged scanner support, label printer fleets, and WMS/ERP connectivity monitoring across the building.",
    },
    {
      name: "Freight Forwarding & Customs Brokerage",
      body:
        "Secure document exchange, EDI connectivity, email security against invoice fraud, and phone systems that hold up to high call volume.",
    },
    {
      name: "Manufacturing & Light Industrial",
      body:
        "Network segmentation between production and office systems, aging equipment isolation, and backup strategies for machine and design files.",
    },
    {
      name: "Professional & Trade Offices",
      body:
        "Microsoft 365, MFA rollouts, and dependable remote access for dispatchers, estimators and account managers.",
    },
  ],

  responseTime:
    "Ontario on-site visits are typically scheduled same-day or next business day, and remote troubleshooting starts the moment you call.",
  driveTime: "Roughly 12–18 minutes from our Montclair office to most Ontario business parks and offices.",

  faqs: [
    {
      q: "Do you support warehouse Wi-Fi and barcode scanners in Ontario?",
      a: "Yes — that's one of the most common requests we get from Ontario. We survey real coverage across the racking, place access points for actual pick paths, keep scanner and label printer firmware standardized, and monitor the network so dropouts get investigated instead of blamed on the devices.",
    },
    {
      q: "Our operation runs two shifts. Can you work outside normal hours?",
      a: "Yes. We schedule disruptive work — switch swaps, patching windows, phone cutovers — around your shift changes and dock schedule. We'll agree on the window in advance rather than pushing changes whenever it's convenient for us.",
    },
    {
      q: "What's the biggest security risk you see in Ontario logistics businesses?",
      a: "Email-based payment fraud. Freight and distribution companies move a lot of invoices, and attackers impersonate carriers or vendors to reroute payments. MFA, email authentication records, banking-change verification steps and staff training address most of it.",
    },
    {
      q: "Can you take over from a provider based in Los Angeles or Orange County?",
      a: "That's a frequent reason Ontario businesses call us. We document your environment, get administrative access transferred properly, fix whatever was left unfinished, and put ongoing monitoring in place — with a technician who's a short drive up the 10 rather than an hour of freeway away.",
    },
  ],
};

export const locationCities: Record<string, City> = {
  montclair,
  "rancho-cucamonga": ranchoCucamonga,
  upland,
  claremont,
  ontario,
};


export function getLocationCity(slug: string): City | undefined {
  return locationCities[slug];
}

export const locationCityList = Object.values(locationCities);
